// Utility functions to get dynamic client information

export interface ClientInfo {
  ipADD: string;
  platform: string;
  userAgent: string;
  timestamp: number;
}

// Get user agent string
const getUserAgent = (): string => {
  return navigator.userAgent;
};

// Get platform information
const getPlatform = (): string => {
  return navigator.platform || 'PC';
};

// Get current timestamp
const getTimestamp = (): number => {
  return Date.now();
};

// Get real browser connection info
const getConnectionInfo = (): string => {
  const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
  
  if (connection) {
    return `effectiveType=${connection.effectiveType || 'unknown'}, downlink=${connection.downlink || 'unknown'}, rtt=${connection.rtt || 'unknown'}`;
  }
  
  return 'connection=unknown';
};

// Get real browser language and timezone
const getLocaleInfo = (): string => {
  const language = navigator.language || 'unknown';
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown';
  return `lang=${language}, tz=${timezone}`;
};

// Get real screen info
const getScreenInfo = (): string => {
  const width = window.screen.width || 'unknown';
  const height = window.screen.height || 'unknown';
  const colorDepth = window.screen.colorDepth || 'unknown';
  return `screen=${width}x${height}, colors=${colorDepth}`;
};

// Get IP info via backend to avoid CORS + rate limits.
const getRealIPAndLocation = async (): Promise<{ ip: string; country: string }> => {
  try {
    const base = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
    const url = `${base}/api/utils/ipinfo`;
    const resp = await fetch(url.startsWith('/api/') ? url : url.replace(/([^:]\/)\/+/g, '$1'), {
      credentials: 'include',
    });
    const json = await resp.json();
    const ip = json?.data?.ip || 'unknown';
    const country = json?.data?.country || 'unknown';
    return { ip, country };
  } catch (error) {
    console.warn('Could not fetch IP info from backend:', error);
    return { ip: 'unknown', country: 'unknown' };
  }
};

// Generate a consistent flow ID based on user agent (won't change every time)
const generateFlowId = (): string => {
  // Create a consistent flow ID based on user agent hash
  const userAgent = getUserAgent();
  let hash = 0;
  for (let i = 0; i < userAgent.length; i++) {
    const char = userAgent.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  // Convert to 5-character string
  return Math.abs(hash).toString(36).substring(0, 5).padStart(5, '0');
};

// Generate real client info string in Cloudflare format
const generateRealClientInfo = async (): Promise<string> => {
  const timestamp = getTimestamp();
  const userAgent = getUserAgent();
  const { ip: realIP, country } = await getRealIPAndLocation();
  const flowId = generateFlowId();
  
  return `fl=${flowId}\nh=www.cloudflare.com\nip=${realIP}\nts=${timestamp}.${Math.floor(Math.random() * 1000)}\nvisit_scheme=https\nuag=${userAgent}\ncolo=NRT\nsliver=none\nhttp=http/2\nloc=${country}\ntls=TLSv1.3\nsni=plaintext\nwarp=off\ngateway=off\nrbi=off\nkex=X25519MLKEM768`;
};

// Get complete client information
export const getClientInfo = async (): Promise<ClientInfo> => {
  return {
    ipADD: await generateRealClientInfo(),
    platform: getPlatform(),
    userAgent: getUserAgent(),
    timestamp: getTimestamp(),
  };
};

// Get just the client info string for API calls
export const getIPInfoString = async (): Promise<string> => {
  return generateRealClientInfo();
};

// Sync version for backward compatibility (without real IP)
export const getIPInfoStringSync = (): string => {
  const timestamp = getTimestamp();
  const userAgent = getUserAgent();
  const connection = getConnectionInfo();
  const locale = getLocaleInfo();
  const screen = getScreenInfo();
  
  return `fl=client_side\nh=www.cloudflare.com\nip=unknown\nts=${timestamp}.${Math.floor(Math.random() * 1000)}\nvisit_scheme=https\nuag=${userAgent}\ncolo=CLIENT_LOC\nsliver=none\nhttp=http/2\nloc=CLIENT_COUNTRY\ntls=TLSv1.3\nsni=plaintext\nwarp=off\ngateway=off\nrbi=off\nkex=X25519MLKEM768\nplatform=${getPlatform()}\n${connection}\n${locale}\n${screen}`;
};

// Get platform for API calls
export const getPlatformString = (): string => {
  return 'PC'; // You can make this dynamic based on device detection
};

// Utility to detect if running on mobile
export const isMobile = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(getUserAgent());
};

// Get appropriate platform string based on device
export const getDynamicPlatform = (): string => {
  return isMobile() ? 'MOBILE' : 'PC';
}; 